import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box, Button, Card, CardContent, Typography,
  Chip, Stack, IconButton, Tooltip,
} from '@mui/material';
import { PersonSearch, PersonAdd, Edit, DeleteOutline, CreditCard, LocalOffer } from '@mui/icons-material';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import FacturaPOSClienteSearchBar from './FacturaPOSClienteSearchBar';
import FacturaPOSNuevoClienteDialog from './FacturaPOSNuevoClienteDialog';
import { POSCliente, POSLinea } from './types';
import { lineasBloqueanEdicionCliente, MSG_CLIENTE_BLOQUEADO_POR_MASTER } from './lineaMasterCliente';
import { formatCurrency } from '../../../utils/formatters';
import { resolverNombreCliente, labelTipoIvaCliente } from './clienteNombre';

interface Props {
  cliente: POSCliente | null;
  lineas?: POSLinea[];
  onClienteSelect: (cliente: POSCliente) => void;
  onClienteClear?: () => void;
  readOnly?: boolean;
  /** Condición de venta del documento; si está informada, el chip refleja este valor (p. ej. al editar en el panel lateral). */
  formaDePago?: string;
  /** Orden de compra del documento; en la tarjeta siempre se muestra la línea (vacío → «Sin dato»). */
  ordenCompra?: string;
}

const FacturaPOSClientePanel: React.FC<Props> = ({
  cliente,
  lineas = [],
  onClienteSelect,
  onClienteClear,
  readOnly = false,
  formaDePago,
  ordenCompra,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchOpen, setSearchOpen] = useState(false);
  const [nuevoClienteOpen, setNuevoClienteOpen] = useState(false);

  const handleSelect = (c: any) => {
    onClienteSelect(c as POSCliente);
    setSearchOpen(false);
  };

  const nroListaLabel =
    cliente?.listaPrecio?.descripcion
    ?? cliente?.listaPrecio?.codigoLista
    ?? cliente?.nroLista;

  /** Nombre mostrado en cabecera: el `nombre` del documento (editable en emisión) tiene prioridad sobre razón social del maestro. */
  const nombreClienteRes = cliente
    ? (String(cliente.nombre ?? '').trim() || resolverNombreCliente(cliente))
    : '';
  const tituloClienteCard = cliente
    ? (nombreClienteRes || `Cliente #${cliente.nroCliente}`)
    : '';

  const condicionVentaChip =
    (formaDePago != null && String(formaDePago).trim() !== '')
      ? String(formaDePago).trim()
      : (cliente?.condVta ?? '').trim();

  const ordenCompraTrim = (ordenCompra ?? '').trim();

  const clienteEdicionBloqueada =
    !readOnly && lineasBloqueanEdicionCliente(lineas, cliente?.nroCliente ?? 0);

  return (
    <Box>
      <ClienteSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelect}
      />

      <FacturaPOSNuevoClienteDialog
        open={nuevoClienteOpen}
        onClose={() => setNuevoClienteOpen(false)}
        onCreated={handleSelect}
      />

      {clienteEdicionBloqueada ? (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
          {MSG_CLIENTE_BLOQUEADO_POR_MASTER}
        </Typography>
      ) : null}

      {!readOnly && !cliente && !clienteEdicionBloqueada && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <FacturaPOSClienteSearchBar
              onClienteSelect={handleSelect}
              disabled={readOnly}
              onClienteInactivo={(msg) => enqueueSnackbar(msg, { variant: 'warning' })}
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAdd />}
            onClick={() => setNuevoClienteOpen(true)}
            sx={{ py: 1, px: 1.5, borderRadius: 2, textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Nuevo cliente
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PersonSearch color="primary" />}
            onClick={() => setSearchOpen(true)}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 2,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
            }}
          >
            Seleccionar cliente
          </Button>
        </Stack>
      )}

      {cliente ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ wordBreak: 'break-word', lineHeight: 1.25, mb: nombreClienteRes ? 0.25 : 0.5 }}
                >
                  {tituloClienteCard}
                </Typography>
                {nombreClienteRes ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    #{cliente.nroCliente}
                  </Typography>
                ) : null}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {cliente.cuit && (
                    <Typography variant="body2" color="text.secondary">
                      CUIT: {cliente.cuit}
                    </Typography>
                  )}
                  {cliente.tipoIva && (
                    <Chip
                      label={labelTipoIvaCliente(cliente.tipoIva)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {nroListaLabel && (
                    <Chip
                      icon={<LocalOffer sx={{ fontSize: '12px !important' }} />}
                      label={`Lista: ${nroListaLabel}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {condicionVentaChip ? (
                    <Chip
                      label={condicionVentaChip}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'action.hover' }}
                    />
                  ) : null}
                </Stack>

                {cliente.credito != null && cliente.credito > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                    <CreditCard sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">Crédito:</Typography>
                    <Typography variant="caption" fontWeight={600} color="success.main">
                      {formatCurrency(cliente.credito)}
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" alignItems="baseline" spacing={2} flexWrap="wrap" useFlexGap mt={0.5}>
                  {cliente.vendedorInfo?.nombreVendedor ? (
                    <Typography variant="caption" color="text.secondary" component="span">
                      Vendedor: {cliente.vendedorInfo.nombreVendedor}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="caption"
                    color={ordenCompraTrim ? 'text.secondary' : 'text.disabled'}
                    component="span"
                  >
                    Orden de compra: {ordenCompraTrim || 'Sin dato'}
                  </Typography>
                </Stack>
              </Box>

              {!readOnly && !clienteEdicionBloqueada && (
                <Stack direction="row" spacing={0} flexShrink={0}>
                  <Tooltip title="Cambiar cliente">
                    <IconButton size="small" onClick={() => setSearchOpen(true)} aria-label="Cambiar cliente">
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {onClienteClear ? (
                    <Tooltip title="Quitar cliente">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={onClienteClear}
                        aria-label="Quitar cliente"
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
};

export default FacturaPOSClientePanel;
